import { ItemOfferCard } from "@/components/home";
import { TitleBand, titleTone } from "@/components/home/SectionRail";
import {
  MENU_CARD_COLUMN_CLASS,
  STOREFRONT_TITLE_REM,
} from "@/domain/storefront-appearance";
import { useBrowsingChannelCatalog } from "@/hooks/use-browsing-channel-catalog";
import { useItemModalParam } from "@/hooks/use-item-modal-param";
import { useStorefrontAppearance } from "@/hooks/use-storefront-appearance";
import { Category, ItemOffer } from "@/types";
import { Badge, Heading, PageContainer, Surface } from "@/components/ui";
import { cn } from "@/lib/cn";

export default function CategoriesList({
  category,
  isLastItem,
  offers,
  onItemClick,
  hideQuantityButton = false,
}: {
  category: Category;
  isLastItem: boolean;
  /** Overrides the offers source; defaults to the browsing channel (INDOOR at a table, else DELIVERY). */
  offers?: ItemOffer[];
  /** Overrides the item click handler; defaults to opening the `item` modal param. */
  onItemClick?: (itemId: string) => void;
  /** Forwarded to ItemOfferCard (indoor info-only cards). */
  hideQuantityButton?: boolean;
}) {
  const { categoriesItemOffers: categoryItemOffers } = useBrowsingChannelCatalog();
  const { openItem } = useItemModalParam();
  const { cardSize, titleSize, menu } = useStorefrontAppearance();

  const resolvedOffers = offers ?? categoryItemOffers[category.id];
  const handleItemClick = onItemClick ?? openItem;

  if (!resolvedOffers) return null;
  if (offers && resolvedOffers.length === 0) return null;

  return (
    <Surface surface="page" id={category.id} className={cn(!isLastItem && "mb-4")}>
      <PageContainer className="relative max-w-shell overflow-hidden rounded-card pb-3">
        <TitleBand titleCard={menu.titleCard}>
          <div className="flex items-center gap-2 w-full">
            <Heading
              level={2}
              tone={titleTone(menu.titleCard.visible)}
              style={{ fontSize: `${STOREFRONT_TITLE_REM[titleSize]}rem` }}
            >
              {category.name}
            </Heading>
            {category.isPromotional ? (
              <Badge variant="solid" tone="danger" uppercase className="ml-auto">
                {(category.promotionalBannerText?.trim() || "PROMO").slice(0, 12)}
              </Badge>
            ) : null}
          </div>
        </TitleBand>
      </PageContainer>
      <PageContainer
        bottom={isLastItem ? "chrome" : "none"}
        className={cn("max-w-shell grid grid-cols-1 gap-3", MENU_CARD_COLUMN_CLASS[cardSize])}
      >
        {resolvedOffers.map((itemOffer, index) => (
          <ItemOfferCard
            key={itemOffer.id}
            itemOffer={itemOffer}
            status={category.status}
            index={index}
            itemOffers={resolvedOffers}
            handleItemClick={handleItemClick}
            itemId={itemOffer.id}
            disabled={category.status === "UNAVAILABLE" || itemOffer.status === "UNAVAILABLE"}
            hideQuantityButton={hideQuantityButton}
          />
        ))}
      </PageContainer>
    </Surface>
  );
}
